// strips the last file extension from a filename
// e.g., "meeting.mp3" -> "meeting", "file.tar.gz" -> "file.tar"
export function stripExtension(filename) {
  if (!filename || typeof filename !== "string") {
    return filename || "";
  }
  // match last extension (dot followed by non-dot/non-slash chars at end)
  return filename.replace(/\.[^/.]+$/, "");
}

// computes sha-256 hash of a file using web crypto API
// returns hex string
export async function computeFileHash(file) {
  if (!file || !(file instanceof Blob)) {
    return null;
  }
  try {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return null;
  }
}

// checks for duplicate files in the jobs store
// returns match info: { type: 'exact' | 'name' | 'size' | 'hash', existing: file } or null
export async function findDuplicate(file, jobsFiles, options = {}) {
  if (!file || !jobsFiles || jobsFiles.length === 0) {
    return null;
  }

  const strippedName = stripExtension(file.name);
  const fileSize = file.size;

  // layer 1: exact filename match (stripped)
  for (const existing of jobsFiles) {
    const existingStripped = stripExtension(existing.displayName);
    if (existingStripped === strippedName) {
      // layer 2: same size = stronger match
      if (existing.sizeBytes === fileSize) {
        // layer 3: compute hash for definitive match (only if sizes match)
        if (options.computeHash !== false) {
          const newHash = await computeFileHash(file);
          // we don't have hashes stored for existing files, so size+name is our best bet
          // for existing server files. for pending uploads with localFile, we could hash.
          if (existing.localFile) {
            const existingHash = await computeFileHash(existing.localFile);
            if (newHash && existingHash && newHash === existingHash) {
              return { type: "hash", existing, confidence: "exact" };
            }
          }
          // size + name match is high confidence
          return { type: "size", existing, confidence: "high" };
        }
        return { type: "size", existing, confidence: "high" };
      }
      // name-only match is lower confidence
      return { type: "name", existing, confidence: "medium" };
    }
  }

  return null;
}

// generates a unique name like mac does: "name (1)", "name (2)", etc.
export function generateUniqueName(baseName, existingNames) {
  if (!existingNames || existingNames.length === 0) {
    return baseName;
  }

  // check if base name is already unique
  if (!existingNames.includes(baseName)) {
    return baseName;
  }

  // extract base without existing (n) suffix if present
  const baseMatch = baseName.match(/^(.+?)\s*\((\d+)\)$/);
  const cleanBase = baseMatch ? baseMatch[1].trim() : baseName;

  // find all existing numbers for this base
  const existingNumbers = new Set([0]);
  const pattern = new RegExp(`^${escapeRegex(cleanBase)}\\s*\\((\\d+)\\)$`);

  for (const name of existingNames) {
    if (name === cleanBase) {
      existingNumbers.add(0);
    }
    const match = name.match(pattern);
    if (match) {
      existingNumbers.add(parseInt(match[1], 10));
    }
  }

  // find next available number
  let num = 1;
  while (existingNumbers.has(num)) {
    num++;
  }

  return `${cleanBase} (${num})`;
}

function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
